import {AfterContentChecked, ChangeDetectorRef, Component, forwardRef, Input, OnInit} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { formControlErrorKeys, formControlErrorMessage } from '@portal-workspace/grow-ui-library';
import { driversLicenceNumberValidator, passportNumberValidator } from '../../validators/validators';
import { tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { MARK, Mark } from '@portal-workspace/grow-ui-library/mark';
import { AbstractControlValueAccessor } from '../abstract-control-value-accessor';
import {getIdTypes, IdentificationComponentValue, IdTypes, normalizeDriversLicenceNumber} from '@portal-workspace/grow-shared-library';
import { InputMaskModule } from '@ngneat/input-mask';
import { MatInputModule } from '@angular/material/input';

import { MatFormFieldModule } from '@angular/material/form-field';
import {DisableControlDirective} from '../../directives/disable-control.directive';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
    selector: 'identification',
    templateUrl: './identification.component.html',
    styleUrls: ['./identification.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IdentificationComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => IdentificationComponent) },
    ],
    standalone: true,
    imports: [FormsModule, MatOptionModule, MatSelectModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, InputMaskModule, DisableControlDirective]
})
export class IdentificationComponent extends AbstractControlValueAccessor<IdentificationComponentValue> implements OnInit, AfterContentChecked, Mark {

  @Input() mandatory = true;
  
  formGroup!: FormGroup<{
    idType: FormControl<string|null>,
    idNumber: FormControl<string|null>
  }>;
  formControlIdType!: FormControl<string|null>;
  formControlIdNumber!: FormControl<string|null>;
  
  idTypes: IdTypes[] = [];

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;
  
  subscriptions: Subscription[] = [];
  
  constructor(private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.formControlIdType = this.formBuilder.control('', this.mandatory ? [Validators.required] : []);
    this.formControlIdNumber = this.formBuilder.control('', this.idNumberValidators(null));

    this.formGroup = this.formBuilder.group({
      idType: this.formControlIdType,
      idNumber: this.formControlIdNumber
    });

    this.idTypes = getIdTypes();

    this.subscriptions.push(
      this.formControlIdType.valueChanges.pipe(
        tap(idType => this.applyIdNumberValidators(idType))
      ).subscribe()
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.pipe(
        tap(() => {
          const idType = this.formControlIdType.value;
          const idNumber = this.normalizedIdNumber(idType, this.formControlIdNumber.value);
          if (idNumber !== this.formControlIdNumber.value) {
            this.formControlIdNumber.setValue(idNumber, { emitEvent: false });
            this.formControlIdNumber.updateValueAndValidity({ emitEvent: false });
          }
          if (this.formGroup.valid && idNumber && idType) {
            this.propagateChange({
              idNumber,
              idType,
            });
          } else {
            this.propagateChange(null);
          }
        })
      ).subscribe()
    )
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  doWriteValue(v: IdentificationComponentValue) {
    if(v) {
      this.formControlIdType.setValue(v.idType, { emitEvent: false });
      this.applyIdNumberValidators(v.idType);
      this.formControlIdNumber.setValue(this.normalizedIdNumber(v.idType, v.idNumber));
    }
  }

  mark() {
    this.formGroup.markAllAsTouched();
  }

  private idNumberValidators(idType: string | null | undefined): ValidatorFn[] {
    const validators: ValidatorFn[] = this.mandatory ? [Validators.required] : [];
    if (idType === 'drivers_licence') {
      validators.push(driversLicenceNumberValidator());
    } else if (idType === 'passport') {
      validators.push(passportNumberValidator());
    }
    return validators;
  }

  private applyIdNumberValidators(idType: string | null | undefined) {
    this.formControlIdNumber.setValidators(this.idNumberValidators(idType));
    this.formControlIdNumber.updateValueAndValidity({ emitEvent: false });
  }

  private normalizedIdNumber(idType: string | null | undefined, idNumber: string | null | undefined): string | null {
    if (idNumber == null) {
      return idNumber ?? null;
    }
    if (idType === 'drivers_licence') {
      return normalizeDriversLicenceNumber(idNumber);
    }
    return idType === 'passport' ? idNumber.trim() : idNumber;
  }

}
